import getConfig from 'next/config';
import dynamic from 'next/dynamic';

import React, { Fragment, Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { withRouter } from 'next/router';
import styled from 'styled-components';
import lozad from 'lozad';
import 'url-search-params-polyfill';
import 'intersection-observer';

import '../utils/createNonce'; // for CSP
import '../utils/polyfills'; // for old browsers
import '../utils/check';

import { login } from '../utils/sensors';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReferralDataSetter from '../components/ReferralDataSetter';
import HeaderLangSelect from '../components/HeaderLangSelect';
import EmergencyNotice from '../components/EmergencyNotice';
import FireFoxWarningTip from '../components/FireFoxWarningTip';
import {
  verifyToken as verifyTokenRequest,
  userLoggedOut as userLoggedOutRequest,
} from '../actions/actionsSet/authcenter';
import {
  fetchInmailData, fetchInmailUnreadCount, fetchInmailReadOrDelet,
  fetchInmailStreamToken,
  addInmailData,
  updateInmailDataCount,
} from '../actions/actionsSet/inmail';
import { checkLogin } from '../utils/auth';
import { readCookie, eraseCookie, createCookie } from '../utils/cookies-browser';
import { getLoginUrl } from '../utils/urlUtils';
import { getHostName, removeQueryStr, getPathWithoutLocale } from '../utils/canonical';
import MobileHeader from '../components/MobileHeader';

require('../static/css/base.css');
require('../node_modules/react-tippy/dist/tippy.css');
require('../node_modules/react-virtualized/styles.css');
require('../static/css/iconfont.css');
require('../static/css/nprogress.css');
require('../static/css/overrides.css');
require('../static/css/lozad.css');

const SensorsdataProvider = dynamic(import('../network/SensorsdataProvider'), {
  ssr: false,
  loading: () => null,
});
const LocalStorageDataProvider = dynamic(import('../network/LocalStorageDataProvider'), {
  ssr: false,
  loading: () => null,
});
const JPushDataProvider = dynamic(import('../network/JPushDataProvider'), {
  ssr: false,
  loading: () => null,
});

const CommonDataProvider = dynamic(import('../network/CommonDataProvider'), {
  ssr: false,
  loading: () => null,
});
const AbstractSocketProvider = dynamic(import('../network/AbstractSocketProvider'), { ssr: false, loading: () => null });
const {
  publicRuntimeConfig: {
    assetPrefix, publicAssetPrefix, brand, buildId: BUILD_ID, inmailWssUrl,
  },
} = getConfig();

const HTTPS = 'https://';

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  ${({ isMobile }) => !isMobile && 'min-width: 1200px;'}
  background: ${({ theme, isGreyBg }) => (isGreyBg ? theme.greyBg : theme.bg)};
`;

const StyledMain = styled.main`
  flex: 1 1 auto;
`;
const StyledContent = styled.div`
  margin: auto;
  width: 100%;
`;

const StyledCommonContent = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  padding: 100px 0 200px;
`;

const InnerContent = styled.div`
  color: ${({ theme }) => theme.fontColor};
  display: flex;
  flex-direction: column;
  transition: color 0.6s ease-out;
  a,
  a:visited,
  a:focus {
    color: ${({ theme }) => theme.fontColor};
    outline: none;
    text-decoration: none;
  }
`;

const mapStateToProps = ({ userInfo, isHideHeadLangSeclet }) => ({
  userInfo,
  isHideHeadLangSeclet,
});
const mapDispatchToProps = {
  verifyToken: verifyTokenRequest,
  userLoggedOut: userLoggedOutRequest,
  fetchInmailData,
  fetchInmailUnreadCount,
  addInmailData,
  updateInmailDataCount,
};

export default
@connect(
  mapStateToProps,
  mapDispatchToProps
)
@withRouter
class Layout extends Component {
  static propTypes = {
    children: PropTypes.node,
    locale: PropTypes.string,
    metaData: PropTypes.shape({
      title: PropTypes.string.isRequired,
      keywords: PropTypes.string,
      description: PropTypes.string,
      ogImage: PropTypes.string,
    }),
    withTradingView: PropTypes.bool,
    withJpush: PropTypes.bool,
    omitHeader: PropTypes.bool,
    omitFooter: PropTypes.bool,
    omitFooterTotalUpdate: PropTypes.bool,
    selectedPair: PropTypes.string,
    isGreyBg: PropTypes.bool,
    isBisicTradePage: PropTypes.bool,
    content: PropTypes.node,
    commonContent: PropTypes.bool,
    router: PropTypes.object,
    hostName: PropTypes.string,
    userInfo: PropTypes.object,
    shouldHeaderFixed: PropTypes.bool,
    verifyToken: PropTypes.func,
    userLoggedOut: PropTypes.func,
    omitVerifyToken: PropTypes.bool,
    locales: PropTypes.object,
    i18n: PropTypes.object,
    isHideHeadLangSeclet: PropTypes.bool,
    omitHeaderLangSelect: PropTypes.bool,
    isShowEmergencyNotice: PropTypes.bool,
    omitFireFoxWarningTip: PropTypes.bool,
    withRecentMarketBar: PropTypes.bool,
    emitAmp: PropTypes.bool,
    isMobile: PropTypes.bool,
    canonical: PropTypes.string,
    noSeo: PropTypes.bool,
    fetchInmailData: PropTypes.func,
    fetchInmailUnreadCount: PropTypes.func,
    addInmailData: PropTypes.func,
    updateInmailDataCount: PropTypes.func,
    fetchInmailDataInPage: PropTypes.func,
    needClear: PropTypes.number,
    withClientApiCache: PropTypes.bool,
    withWebAuthn: PropTypes.bool,
  };

  static defaultProps = {
    locale: 'en',
    i18n: {},
    metaData: {},
    withTradingView: false,
    omitHeader: false,
    omitFooter: false,
    omitFooterTotalUpdate: false,
    selectedPair: 'BTC_USDT',
    isGreyBg: false,
    isBisicTradePage: false,
    content: null,
    commonContent: false,
    children: null,
    router: { asPath: '/' },
    hostName: 'www.binance.com',
    shouldHeaderFixed: false,
    omitVerifyToken: false,
    omitHeaderLangSelect: false,
    isShowEmergencyNotice: true,
    omitFireFoxWarningTip: true,
    withRecentMarketBar: false,
    emitAmp: false,
    isMobile: false,
    canonical: '',
    noSeo: false,
    needClear: -1,
    fetchInmailData: () => {},
    fetchInmailUnreadCount: () => {},
    addInmailData: () => {},
    updateInmailDataCount: () => {},
    fetchInmailDataInPage: () => {},
    refeshInmailData: () => {},
    withClientApiCache: false,
    withWebAuthn: true,
  };

  state = { token: '' };

  static getDerivedStateFromProps(nextProps, prevState) {
    const userId = nextProps.userInfo?.userData?.userId;

    if (typeof window !== 'undefined') {
      // set chat pathname
      window.localStorage.setItem('chatGetPath', nextProps.router.pathname);
      const timer = setInterval(() => {
        if (window.setChatPath) {
          clearInterval(timer);
          window.setChatPath(nextProps.router.pathname);
        }
      }, 100);
    }

    if (prevState?.userId !== userId) {
      login(userId);
    }
    return {
      userId,
    };
  }

  async componentDidMount() {
    // NOTE: get screen size first
    this.onresize();
    if (this.props.isMobile) {
      window.addEventListener('resize', this.onresize);
    }

    const locale = this.props.router.query.locale || 'en';
    createCookie('lang', locale.toUpperCase());
    if (this.props.omitVerifyToken) return;
    const {
      verifyToken,
      userLoggedOut,
      router: { asPath, pathname },
    } = this.props;
    const cr00 = readCookie('cr00');
    if (!cr00) {
      eraseCookie('logined');
      userLoggedOut();
      return;
    }
    const verifiedLoggedIn = await verifyToken();
    if (!verifiedLoggedIn) {
      const { redirect, fallbackUrl = asPath } = checkLogin(pathname) || {};
      if (redirect) {
        window.location.href = getLoginUrl(locale, fallbackUrl);
      }
    } else {
      // update login status when login from other binance site
      createCookie('logined', 'y', 7);
      this.props.fetchInmailUnreadCount();

      fetchInmailStreamToken().then((data) => {
        this.setState({ token: data });
      });
      this.props.fetchInmailData({
        page: 1,
        rows: 20,
        status: 1,
      }, true).then((data) => {
        this.setState({ messages: data.data });
        this.setState({ messageCount: data.total });
      });
      // this.onMessage()
    }
  }


  componentDidUpdate({ needClear }) {
    if (this.props.needClear !== needClear) {
      this.props.fetchInmailData({
        page: 1,
        rows: 20,
        status: 1,
      }, true).then((data) => {
        this.setState({ messages: data.data });
        this.setState({ messageCount: data.total });
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onresize);
  }

  onresize = () => {
    if (window.screen.width > 578) {
      this.setState({ screenIsLarge: true });
    } else {
      this.setState({ screenIsLarge: false });
    }
  };

  _getCanonicalUrl = () => {
    const { hostName, router, canonical } = this.props;
    let _canonical = canonical;

    if (!_canonical) {
      const canonicalUrl = `${getHostName(hostName)}${removeQueryStr(router.asPath)}`;
      _canonical = !canonicalUrl.startsWith(HTTPS) ? `${HTTPS}${canonicalUrl}` : canonicalUrl;
    }

    return _canonical;
  };

  onMessage = (data) => {
    if (data.messageType === 'MESSSAGE_TEXT') {
      data.insertTime = data.message.insertTime;
      data.id = data.message.id;
      data.moduleId = data.message.moduleId;
      this.props.addInmailData(data);
      const newMsg = [data, ...this.state.messages];
      if (newMsg.length > 10) newMsg.pop();
      this.setState({ messages: newMsg });
    }
    if (data.messageType === 'UNREAD_MESSAGE_COUNT') {
      this.props.updateInmailDataCount(data.message);
      this.setState({ messageCount: data.message.userNum });
    }
  }

  refeshInmailData = () => {
    this.props.fetchInmailData({
      page: 1,
      rows: 20,
      status: 1,
    }, true);
  }

  onreadInmail = (id, fetch) => {
    this.props.fetchInmailDataInPage();
    fetchInmailReadOrDelet({
      id,
      status: 2,
    }).then(() => {
      const messages = this.state.messages.filter(x => x.id !== id);
      this.setState({ messages });
      // this.setState({ messageCount: this.state.messageCount - 1 });
      if (fetch) {
        this.props.fetchInmailData({
          page: 1,
          rows: 20,
          status: 1,
        }, true);
      }
    });
  }

  render() {
    const {
      children,
      metaData,
      withTradingView,
      withJpush,
      omitHeader,
      omitFooter,
      omitHeaderLangSelect,
      omitFooterTotalUpdate,
      selectedPair,
      isGreyBg,
      isBisicTradePage,
      content,
      commonContent,
      router,
      hostName,
      shouldHeaderFixed,
      locales,
      locale,
      isHideHeadLangSeclet,
      isShowEmergencyNotice,
      omitFireFoxWarningTip,
      withRecentMarketBar,
      emitAmp,
      isMobile,
      withClientApiCache,
      withWebAuthn,
    } = this.props;
    const { token, messageCount, messages } = this.state;
    // const canonicalUrl = `${getHostName(hostName)}${removeQueryStr(router.asPath)}`;
    // const canonicalUrlWithProtocol = !canonicalUrl.startsWith(HTTPS)
    //   ? `${HTTPS}${canonicalUrl}`
    //   : canonicalUrl;
    const url = `${HTTPS}${hostName}${router.asPath}`;
    const ampHtmlUrl = `${hostName}/${locale}/amp${getPathWithoutLocale(
      removeQueryStr(router.asPath)
    )}`;
    const ampHtmlUrlWithProtocol = !ampHtmlUrl.startsWith(HTTPS)
      ? `${HTTPS}${ampHtmlUrl}`
      : ampHtmlUrl;
    const ogImage = metaData.ogImage || `${publicAssetPrefix}/static/images/common/ogImage.jpg`;

    const showMoblieHeader = isMobile && !this.state.screenIsLarge;

    typeof window !== 'undefined' && setTimeout(() => lozad().observe());

    return (
      <Fragment>
        <Head>
          <title>{metaData.title}</title>
          <link
            rel="shortcut icon"
            type="image/x-icon"
            href={`${assetPrefix}/static/images/common/favicon.ico`}
          />
          <link href={this._getCanonicalUrl()} rel="canonical" />
          {emitAmp ? <link href={ampHtmlUrlWithProtocol} rel="amphtml" /> : null}
          {metaData.keywords ? <meta name="keywords" content={metaData.keywords} /> : null}
          <meta property="og:url" content={url} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={metaData.metaTitle || metaData.title} />
          <meta property="og:site_name" content={brand} />
          <meta property="og:image" content={ogImage} />
          <meta property="twitter:title" content={metaData.metaTitle || metaData.title} />
          <meta property="twitter:site" content={brand} />
          <meta property="twitter:image" content={ogImage} />
          <meta property="twitter:image:src" content={ogImage} />
          <meta property="twitter:card" content="summary_large_image" />
          <meta name="robots" content={metaData.noIndex ? 'noindex' : 'index'} />
          {metaData.description ? (
            <Fragment>
              <meta name="description" content={metaData.description} />
              <meta property="og:description" content={metaData.description} />
              <meta property="twitter:description" content={metaData.description} />
            </Fragment>
          ) : null}
          {/* cover next default viwport(width=device-width,minimum-scale=1,initial-scale=1) since v8.0.4 */}
          {!isMobile ? <meta name="viewport" content="width=1200, user-scalable=yes" /> : null}
          {/* no cdn/asset prefix because content could be dynamic. */}
          {withTradingView ? (
            <script
              src={`${assetPrefix}/static/tradingview/charting_library/charting_library.min.js`}
              async={true}
            />
          ) : null}
          {withJpush ? (
            <script
              src={`${assetPrefix}/static/jpush/jpush-sdk-web.min.js?v=${BUILD_ID}`}
              async={true}
            />
          ) : null}
          {withJpush ? (
            <script src={`${assetPrefix}/static/jpush/helper.js?v=${BUILD_ID}`} async={true} />
          ) : null}
          {withJpush ? (
            <script src={`${assetPrefix}/static/jpush/md5.min.js?v=${BUILD_ID}`} async={true} />
          ) : null}

          {withWebAuthn ? (
            <>
              {<script id="base64js" src={`${assetPrefix}/static/webauth/base64js.js`} async={true} />}
              {<script id="base64url" src={`${assetPrefix}/static/webauth/base64url.js`} async={true} />}
              {<script id="webauthn" src={`${assetPrefix}/static/webauth/webauthn.js`} async={true} />}
            </>
          ) : null}
        </Head>
        <SensorsdataProvider />
        <LocalStorageDataProvider />
        {withJpush ? <JPushDataProvider /> : null}
        <CommonDataProvider withClientApiCache={withClientApiCache} />
        <StyledLayout
          isGreyBg={isGreyBg}
          className="layout"
          isBisicTradePage={isBisicTradePage}
          isMobile={showMoblieHeader}
        >
          <ReferralDataSetter />
          {!omitHeaderLangSelect && (
            <HeaderLangSelect isHideHeadLangSeclet={isHideHeadLangSeclet} />
          )}
          {
            token ? <AbstractSocketProvider url={`${inmailWssUrl}${token}`} keepAlive={true} onMessage={this.onMessage} /> : null
          }
          {!omitHeader &&
            (!showMoblieHeader ? (
              <Header
                inmailFreshData={messages}
                inmailUnreadData={messageCount}
                onreadInmail={this.onreadInmail}
                selectedPair={selectedPair}
                showScaleAnimation={shouldHeaderFixed}
                isHideHeadLangSeclet={isHideHeadLangSeclet}
                withClientApiCache={withClientApiCache}
              />
            ) : (
              <MobileHeader />
            ))}

          <EmergencyNotice
            shouldHeaderFixed={shouldHeaderFixed}
            isShowEmergencyNotice={isShowEmergencyNotice}
          />

          {!omitFireFoxWarningTip && <FireFoxWarningTip />}
          <StyledMain>
            {content ||
              (commonContent ? (
                <StyledCommonContent>{children}</StyledCommonContent>
              ) : (
                <StyledContent
                  isBisicTradePage={isBisicTradePage}
                  isShowEmergencyNotice={isShowEmergencyNotice}
                >
                  <InnerContent>{children}</InnerContent>
                </StyledContent>
              ))}
          </StyledMain>
          {!omitFooter
            // NOTE:
            // If the screen size is mobile, we won't show
            // the footer because our footer is not responsive yet.
            && this.state.screenIsLarge
            && (
              <Footer
                locales={locales}
                isBisicTradePage={isBisicTradePage}
                omitFooterTotalUpdate={omitFooterTotalUpdate}
                withRecentMarketBar={withRecentMarketBar}
              />
            )
          }
        </StyledLayout>
      </Fragment>
    );
  }
}
